'use client'

import { useCallback, useEffect, useReducer, useState, useSyncExternalStore } from 'react'
import { useChatSender, type ChatSendTransport, type Outgoing } from '@/hooks/use-chat-sender'
import { useSantaVoicePlayer, type SantaReply, type SpeechSource } from '@/hooks/use-santa-voice-player'
import type { PublicChatAppLabels } from '@/lib/free-santa'
import { INVALID_SESSION_CODE, openPublicChat, sendPublicChat } from '@/lib/public-chat/api'
import { storePublicChatChildName } from '@/lib/public-chat/child-name'
import { PRODUCT_EVENTS } from '@/lib/product-analytics'
import { SantaChatApiError } from '@/lib/santa-chat/api'
import { chatReducer, INITIAL_CHAT_STATE, type ChatAction } from '@/lib/santa-chat/chat-state'
import type { PublicChatMessageResult } from '@/server/application/santa-chat/public/public-chat-contract'
import { capturePublicChatEvent } from './funnel-events'
import { createSessionTokenStore, deviceUsedOn, type SessionTokenStore } from './public-chat-storage'
import { openingOf, turnOutcomeOf, type ClosingEvent } from './public-chat-turns'
import { INITIAL_PUBLIC_SESSION, nextMessageNumber, publicSessionReducer, type PublicSession } from './public-session'

export type { ClosingOffer } from './public-chat-turns'

/** El chat público no guarda nada: una nota cuya voz no está en memoria no tiene de dónde pedirla. */
const noSpeech: SpeechSource = async () => ({ audioBase64: '' })

/**
 * Qué pantalla toca: `unknown` hasta leer el almacenamiento del navegador (el
 * HTML de servidor no sabe si hay sesión ni si el dispositivo ya habló hoy),
 * el consentimiento, el cierre de un dispositivo que ya habló hoy, o el chat.
 */
export type PublicChatPhase = 'unknown' | 'consent' | 'usedToday' | 'chat'

type FunnelEvent = (typeof PRODUCT_EVENTS)[
  | 'PUBLIC_CHAT_STARTED'
  | 'PUBLIC_CHAT_MESSAGE_SENT'
  | 'PUBLIC_CHAT_SESSION_COMPLETED'
  | 'PUBLIC_CHAT_CLOSED_FOR_TODAY']

const CLOSING_EVENT: Record<Exclude<ClosingEvent, null>, FunnelEvent> = {
  completed: PRODUCT_EVENTS.PUBLIC_CHAT_SESSION_COMPLETED,
  closedForToday: PRODUCT_EVENTS.PUBLIC_CHAT_CLOSED_FOR_TODAY,
}

/** El servidor no sabe nada del almacenamiento del navegador: hasta hidratar, ni token ni marca (`undefined`). */
const unknownOnServer = () => undefined

/** El token de la sesión y la marca del día del dispositivo, leídos de su almacén. */
function useStoredSession() {
  const [sessionToken] = useState<SessionTokenStore>(createSessionTokenStore)
  const token = useSyncExternalStore<string | null | undefined>(sessionToken.subscribe, sessionToken.read, unknownOnServer)
  const usedToday = useSyncExternalStore(sessionToken.subscribe, () => deviceUsedOn.usedTodayWithoutSession(sessionToken), () => false)
  return { sessionToken, token, usedToday }
}

function phaseOf(session: PublicSession, token: string | null | undefined, usedToday: boolean): PublicChatPhase {
  if (session.started || token) return 'chat'
  if (token === undefined) return 'unknown'
  return usedToday ? 'usedToday' : 'consent'
}

/** Cada voz espera a la anterior (la despedida suena cuando acaba la respuesta). */
async function sayInOrder(play: (reply: SantaReply) => Promise<void>, voices: SantaReply[]): Promise<void> {
  for (const reply of voices) await play(reply)
}

/**
 * El chat público montado sobre el estado, el reproductor y el envío del chat
 * del niño: misma pantalla, mismos gestos, mismas voces. Solo es suyo el
 * transporte (`/api/santa-chat/public`, con el token firmado), la sesión
 * (cuántos mensajes quedan, cómo acaba) y el funnel.
 */
export function usePublicSantaChat(locale: string, labels: PublicChatAppLabels) {
  const [state, dispatch] = useReducer(chatReducer, INITIAL_CHAT_STATE)
  const voice = useSantaVoicePlayer(dispatch, noSpeech)
  const { sessionToken, token, usedToday } = useStoredSession()
  const [session, update] = useReducer(publicSessionReducer, INITIAL_PUBLIC_SESSION)
  const phase = phaseOf(session, token, usedToday)

  const track = useCallback(
    (event: FunnelEvent, properties: { n?: number } = {}) => capturePublicChatEvent(event, { ...properties, locale }),
    [locale]
  )

  // Un token de una visita anterior (recargar la página): el chat sigue sin pedir otra apertura.
  const resumed = Boolean(token) && !session.started
  useEffect(() => {
    if (resumed) dispatch({ type: 'resumed' })
  }, [resumed])

  const open = async (name: string | null, turnstileToken: string | null) => {
    update({ type: 'openStarted' })
    const result = await openPublicChat({ name, locale, turnstileToken, deviceUsedOn: deviceUsedOn.read() }).catch(() => null)
    if (!result) {
      update({ type: 'openFailed', error: labels.elvesError })
      return
    }
    const { opening, reply } = openingOf(result)
    if (result.kind === 'opened') {
      deviceUsedOn.markToday()
      // El nombre (del formulario o el que diga luego) es lo único que pasa al alta de perfil (AC2.3).
      storePublicChatChildName(name)
      sessionToken.write(result.token)
      track(PRODUCT_EVENTS.PUBLIC_CHAT_STARTED)
      update({ type: 'opened', quota: { left: result.messagesLeft, total: result.sessionMessages }, closing: null })
    } else {
      // Dispositivo o IP ya usados hoy, o taller cerrado: Papá Noel lo dice y el chat se cierra.
      if (result.kind === 'closed_for_today') track(PRODUCT_EVENTS.PUBLIC_CHAT_CLOSED_FOR_TODAY)
      update({ type: 'opened', quota: null, closing: 'full' })
    }
    dispatch({ type: 'opened', opening })
    void voice.play(reply)
  }

  const transport: ChatSendTransport<PublicChatMessageResult, undefined> = {
    prepare: () => {
      const n = nextMessageNumber(session)
      track(PRODUCT_EVENTS.PUBLIC_CHAT_MESSAGE_SENT, n === null ? {} : { n })
      return undefined
    },
    request: (outgoing: Outgoing) => {
      // El micrófono del chat público no graba: solo llega texto.
      if (outgoing.inputMode !== 'text') return Promise.reject(new Error('El chat público solo envía texto'))
      return sendPublicChat(sessionToken.read() ?? '', outgoing.text)
    },
    apply: (result, sent) => {
      const outcome = turnOutcomeOf(result, sent.localId, sent.outgoing.inputMode === 'text' ? sent.outgoing.text : '')
      outcome.actions.forEach((action: ChatAction) => dispatch(action))
      void sayInOrder(voice.play, outcome.voices)
      if (outcome.childName) storePublicChatChildName(outcome.childName)
      sessionToken.write(outcome.nextToken)
      if (outcome.closing) update({ type: 'closed', offer: outcome.closing })
      else update({ type: 'turnCounted', left: outcome.messagesLeft })
      if (outcome.closingEvent) track(CLOSING_EVENT[outcome.closingEvent])
    },
    handleError: (error, localId) => {
      if (!(error instanceof SantaChatApiError) || error.code !== INVALID_SESSION_CODE) return undefined
      // Sin token la puerta vuelve a decidir: el mismo día que el dispositivo ya habló, el cierre; otro día, el consentimiento.
      dispatch({ type: 'turnFailed', localId })
      sessionToken.write(null)
      update({ type: 'forgotten' })
      return 'dropped'
    },
  }
  const { send, resend } = useChatSender(transport, dispatch)
  const sendText = useCallback((text: string) => void send({ inputMode: 'text', text }), [send])

  return { phase, state, voice, session, open, send: sendText, resend } as const
}
